import { isCvImportReviewCancelled, isCvImportSourceChanged } from '../../../lib/cv-import-review';
/**
 * Create-CV flow controller: every decision the screen makes, in one place.
 *
 * The native and web screens are pure rendering over this hook. Anything that
 * answers "which step are we on", "what does the button say", "can it be
 * pressed", "what happens when it is" lives here, so the two twins cannot drift
 * into two different products.
 *
 * The flow is a WIZARD, not a pair of tabs:
 *
 *   Step 1  Start from  →  an existing CV / an upload / your profile / blank
 *   Step 2  Tailor      →  as it is / a CV skill / a job description
 *
 * Step 2 always operates on what step 1 produced, which is exactly why it can
 * not be a sibling tab: "tailor" with no starting point is not a thing.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import type { ParsedCvData, SkillSummary } from '@tabbio/types';
import { useToast } from '../../../hooks/useToast';
import { useHaptics } from '../../../hooks/useHaptics';
import { useTailoredCVs } from '../../../hooks/useTailoredCvApi';
import { useCatalogAutoApplyOverview } from '../../../hooks/useJobsApi';
import { buildTailoredCvRoute } from '../../../lib/cv-target-route';
import { trackProductEvent } from '../../../lib/product-analytics';
import { buildCvListWithMainFirst } from '../../../lib/cv-main-default';
import type {
  ResumeComposerDraft,
  ResumeImportComposerParsed,
} from '../use-resume-import-composer';
import { prettySkillName } from '../../skills/skill-name';
import { getSkill, type SkillDetailWithConfig } from '../../../lib/skills-api';
import {
  declaredInputsComplete,
  declaredInputsPayload,
  skillInputFields,
} from '../../skills/skill-taxonomy';
import {
  INITIAL_CREATE_CV_STATE,
  createCvStepCount,
  createCvStepIndex,
  readSkillParam,
  readSourceParam,
  resolveCreateCvPrimaryAction,
  resolveCreateCvSourceStepAction,
  resolveCreateCvStep,
  sourceSupportsTailoring,
  stateWantsJobDescription,
  validateCreateCv,
  type CreateCvSource,
  type CreateCvState,
  type CreateCvStep,
  type CreateCvTailorMode,
} from './create-cv-machine';
import { resolveCreateCvErrorMessage } from './create-cv-errors';
import { useCreateCvSubmit } from './use-create-cv-submit';

const EMPTY_RESUME_DRAFT: ResumeComposerDraft = {
  hasText: false,
  hasFile: false,
};

function firstParam(value?: string | string[]) {
  return (Array.isArray(value) ? value[0] : value)?.trim() || undefined;
}

/** A type alias, not an interface: expo-router's `Route` constraint needs the
 *  implicit index signature only type aliases carry. */
type CreateCvFlowParams = {
  audience?: string | string[];
  source?: string | string[];
  sourceCvId?: string | string[];
  skillId?: string | string[];
  title?: string | string[];
  prompt?: string | string[];
};

export function useCreateCvFlow() {
  const { t } = useTranslation('seeker');
  const router = useRouter();
  const toast = useToast();
  const haptics = useHaptics();
  const params = useLocalSearchParams<CreateCvFlowParams>();

  const { submit, isSubmitting, isGenerating } = useCreateCvSubmit();
  const cvs = useTailoredCVs();
  const autoApplyOverview = useCatalogAutoApplyOverview();
  const defaultCvId =
    autoApplyOverview.data?.rules.find((rule) => rule.key === 'auto-apply')
      ?.settings.defaultCvId ?? null;
  const orderedCvs = useMemo(
    () =>
      buildCvListWithMainFirst(
        cvs.data ?? [],
        defaultCvId,
        t(defaultCvId ? 'cv.list.profileTitle' : 'cv.list.masterTitle'),
      ),
    [cvs.data, defaultCvId, t],
  );

  const [state, setState] = useState<CreateCvState>(() => {
    const sourceCvId = firstParam(params.sourceCvId);
    const skillId = firstParam(params.skillId);
    const next: CreateCvState = {
      ...INITIAL_CREATE_CV_STATE,
      title: firstParam(params.title) ?? '',
      jobDescription: firstParam(params.prompt) ?? '',
      ...readSourceParam(firstParam(params.source)),
      ...readSkillParam(skillId),
      ...(sourceCvId ? { source: 'existing' as const, sourceCvId } : {}),
    };
    // A link that already names both the CV and the skill has answered step 1;
    // dropping the user on it again would just be a screen to click through.
    if (sourceCvId && skillId) next.step = 'tailor';
    return next;
  });

  const [selectedSkill, setSelectedSkill] = useState<SkillSummary | null>(null);
  const [importEvidenceId, setImportEvidenceId] = useState<string | undefined>();
  const [parsed, setParsed] = useState<ParsedCvData | null>(null);
  /**
   * The picked skill's DETAIL. The picker only ever sees a `SkillSummary`,
   * which carries no `inputsSchema` - so without this the flow could not know a
   * skill declares a required question, and the run died server-side with
   * SKILL_INPUT_REQUIRED on every press.
   */
  const [skillDetail, setSkillDetail] = useState<
    SkillDetailWithConfig | null | undefined
  >(undefined);
  const [skillFieldValues, setSkillFieldValues] = useState<
    Record<string, string>
  >({});
  /** Last failure, kept on screen instead of only flashing past in a toast. */
  const [submitError, setSubmitError] = useState<string | null>(null);
  /**
   * The composer's draft, reported outwards.
   *
   * It used to be private, which is why attaching a CV did nothing until the
   * composer's own send arrow was pressed while the wizard's Continue button
   * sat greyed out beside it. Continue now sees the draft and can submit it.
   */
  const [resumeDraft, setResumeDraft] =
    useState<ResumeComposerDraft>(EMPTY_RESUME_DRAFT);
  const [resumeBusy, setResumeBusy] = useState(false);
  const composerSubmit = useRef<(() => void) | null>(null);

  const patch = useCallback(
    (next: Partial<CreateCvState>) => setState((prev) => ({ ...prev, ...next })),
    [],
  );

  // Preselect the newest CV, but only while "An existing CV" is the answer:
  // leaving a stray `sourceCvId` behind on the profile/blank sources would make
  // the plan resolve to a duplicate of a CV the user never picked.
  useEffect(() => {
    if (state.source !== 'existing') return;
    if (state.sourceCvId) return;
    const first = orderedCvs[0]?.id;
    if (first) patch({ sourceCvId: first });
  }, [orderedCvs, patch, state.source, state.sourceCvId]);

  // A skill's declared run-screen fields live on the detail, not the summary.
  useEffect(() => {
    const skillId = state.skillId;
    if (!skillId) {
      setSkillDetail(undefined);
      return;
    }
    let alive = true;
    setSkillDetail(undefined);
    void getSkill(skillId)
      .then((detail) => {
        if (alive) setSkillDetail(detail);
      })
      .catch(() => {
        // A failed lookup must not strand the button: fall back to the
        // no-declared-inputs behaviour the flow had before.
        if (alive) setSkillDetail(null);
      });
    return () => {
      alive = false;
    };
  }, [state.skillId]);

  // ── Derived shape ──────────────────────────────────────────────────────────

  const step: CreateCvStep = resolveCreateCvStep(state);
  const totalSteps = createCvStepCount(state.source);
  const stepIndex = createCvStepIndex({ ...state, step });
  const hasTailorStep = sourceSupportsTailoring(state.source);

  const skillDetailLoading = Boolean(state.skillId) && skillDetail === undefined;
  const skillInputFieldList = useMemo(
    () => (skillDetail ? skillInputFields(skillDetail.inputsSchema) : null),
    [skillDetail],
  );
  // Unanswered required fields block the button here rather than 403-ing at the
  // server. While the detail is still loading we cannot know, so we hold.
  const skillInputsComplete =
    state.tailorMode !== 'skill'
      ? true
      : !skillDetailLoading &&
        declaredInputsComplete(skillInputFieldList, skillFieldValues);

  const validation = useMemo(
    () =>
      validateCreateCv(state, {
        selectedSkill,
        skillInputsComplete,
        skillCollectsOwnInputs: Boolean(skillInputFieldList),
      }),
    [state, selectedSkill, skillInputsComplete, skillInputFieldList],
  );
  const uploadIncomplete = state.source === 'upload' && !parsed;
  // A v2 skill declares its own job-post field, so the flow's separate JD box
  // would be a second place to type the same thing.
  const needsJobDescription =
    !skillInputFieldList && stateWantsJobDescription(state, selectedSkill);

  // Unsent draft content counts as ready: pressing Continue submits it rather
  // than leaving a pressed-looking button that does nothing.
  const resumeDraftPending = resumeDraft.hasText || resumeDraft.hasFile;
  const primary = useMemo(
    () =>
      resolveCreateCvPrimaryAction(state, {
        step,
        resumeReady: !uploadIncomplete || resumeDraftPending,
        canSubmit: validation.canSubmit,
        busy: isSubmitting || resumeBusy,
      }),
    [
      isSubmitting,
      resumeBusy,
      resumeDraftPending,
      state,
      step,
      uploadIncomplete,
      validation.canSubmit,
    ],
  );

  // ── Step 1 edits ───────────────────────────────────────────────────────────

  const setSource = useCallback(
    (source: CreateCvSource) => {
      haptics.trigger('light');
      setState((prev) => {
        // Re-tapping the selected source must not throw away a tailoring choice
        // a deep link (or the user, before stepping back) already made.
        if (prev.source === source) return prev;
        return {
          ...prev,
          source,
          sourceCvId: source === 'existing' ? prev.sourceCvId : null,
          // The CV the previous upload produced belongs to a source the user
          // just walked away from; tailoring it would be tailoring the wrong CV.
          importedCvId: null,
          tailorMode: 'none',
          skillId: null,
        };
      });
      if (state.source !== source) {
        setSelectedSkill(null);
        setSubmitError(null);
        setResumeDraft(EMPTY_RESUME_DRAFT);
      }
    },
    [haptics, state.source],
  );

  const setSourceCv = useCallback(
    (cvId: string) => patch({ sourceCvId: cvId, source: 'existing' }),
    [patch],
  );

  const setTitle = useCallback((title: string) => patch({ title }), [patch]);

  const handleResumeParsed = useCallback(
    ({ parsed: result, importEvidenceId }: ResumeImportComposerParsed) => {
      setParsed(result);
      setImportEvidenceId(importEvidenceId);
      // A parsed resume IS the end of the read, and it also unmounts the
      // composer (the step swaps it for the "Resume read" row), so the composer
      // never gets to report that it stopped working. Clearing both here is
      // what keeps Continue from spinning forever on a finished upload.
      setResumeBusy(false);
      setResumeDraft(EMPTY_RESUME_DRAFT);
      setState((prev) => {
        if (prev.title.trim()) return prev;
        const derived = result.title || result.name;
        return derived ? { ...prev, title: derived } : prev;
      });
    },
    [],
  );

  const handleResumeDraftChange = useCallback(
    (draft: ResumeComposerDraft) => setResumeDraft(draft),
    [],
  );
  const handleResumeBusyChange = useCallback(
    (busy: boolean) => setResumeBusy(busy),
    [],
  );

  const resetResume = useCallback(() => {
    setParsed(null);
    setImportEvidenceId(undefined);
    setSubmitError(null);
    // The composer unmounts while a parsed resume is showing, so it never gets
    // to report an empty draft. Left stale, Continue would look pressable and
    // then submit nothing.
    setResumeDraft(EMPTY_RESUME_DRAFT);
    // Replacing the resume must not leave the previous import as the CV the
    // next submit tailors.
    patch({ importedCvId: null });
  }, [patch]);

  // ── Step 2 edits ───────────────────────────────────────────────────────────

  const setTailorMode = useCallback(
    (mode: CreateCvTailorMode) => {
      haptics.trigger('light');
      patch({ tailorMode: mode });
    },
    [haptics, patch],
  );

  const setSkill = useCallback(
    (skill: SkillSummary) => {
      setSelectedSkill(skill);
      setSkillFieldValues({});
      setSubmitError(null);
      patch({ skillId: skill.skillId, tailorMode: 'skill' });
    },
    [patch],
  );

  const setSkillFieldValue = useCallback((id: string, value: string) => {
    setSkillFieldValues((prev) => ({ ...prev, [id]: value }));
    setSubmitError(null);
  }, []);

  const setJobDescription = useCallback(
    (jobDescription: string) => patch({ jobDescription }),
    [patch],
  );

  // ── Titles and recap ───────────────────────────────────────────────────────

  const sourceSummary = useMemo(() => {
    switch (state.source) {
      case 'existing': {
        if (!state.sourceCvId) return '';
        const cv = orderedCvs.find((item) => item.id === state.sourceCvId);
        return t('cv.create.recap.existing', {
          name: cv?.title || t('cv.create.source.untitledCv'),
        });
      }
      case 'upload':
        return t('cv.create.recap.upload');
      case 'profile':
        return t('cv.create.recap.profile');
      default:
        return '';
    }
  }, [orderedCvs, state.source, state.sourceCvId, t]);

  /**
   * The name the CV gets when the user leaves the field blank — which the field
   * invites them to do. Named after what it was BUILT from, so an untouched
   * list still reads as something rather than four "Untitled CV" rows.
   */
  const resolvedTitle = useMemo(() => {
    const explicit = state.title.trim();
    if (explicit) return explicit;
    if (selectedSkill) return selectedSkill.name;
    if (state.source === 'blank') return t('cv.create.defaultTitle.blank');
    if (state.source === 'profile') return t('cv.create.defaultTitle.profile');
    const sourceCv = orderedCvs.find((item) => item.id === state.sourceCvId);
    if (sourceCv?.title) return t('cv.create.copyTitle', { name: sourceCv.title });
    return t('cv.tailored.importDefaultTitle');
  }, [orderedCvs, selectedSkill, state.source, state.sourceCvId, state.title, t]);

  // ── Navigation ─────────────────────────────────────────────────────────────

  const create = useCallback(async () => {
    haptics.trigger('light');
    setSubmitError(null);
    try {
      const result = await submit({
        state,
        title: resolvedTitle,
        parsed,
        importEvidenceId,
        handoff: null,
        skillInputs: skillInputFieldList
          ? declaredInputsPayload(skillInputFieldList, skillFieldValues)
          : undefined,
        // Recorded before the tailoring that follows can fail, so pressing the
        // button again resumes from the tailoring instead of importing the same
        // resume a second time and leaving a stray CV behind.
        //
        // Guarded on the source still being the upload: the write is in flight
        // while the screen stays interactive, and a user who steps back and
        // picks "An existing CV" meanwhile must not have this late callback
        // re-attach the abandoned import and tailor that instead.
        onCvImported: (cvId) =>
          setState((prev) =>
            prev.source === 'upload' ? { ...prev, importedCvId: cvId } : prev,
          ),
      });
      haptics.trigger('success');
      // GA4 counts creation from the API's own record; this copy is for
      // PostHog, where the plan and source explain how the CV came to be.
      const tailored =
        result.plan.kind === 'generate' ||
        ('jobDescription' in result.plan && Boolean(result.plan.jobDescription));
      trackProductEvent(tailored ? 'cv_tailored_created' : 'cv_created', {
        source: state.source,
        plan: result.plan.kind,
        skillId: state.skillId ?? null,
      });
      if (result.plan.kind === 'skill_run') {
        toast.success(
          t('cv.create.success.skillTitle'),
          t('cv.create.success.skillBody', {
            skill:
              selectedSkill?.name ?? prettySkillName(result.plan.skillId),
          }),
        );
      }
      router.replace(buildTailoredCvRoute(result.cvId));
    } catch (error) {
      if (isCvImportReviewCancelled(error)) return;
      const message = isCvImportSourceChanged(error) ? t("cv.importEvidence.sourceChanged") : resolveCreateCvErrorMessage(error, t);
      // Kept on screen as well as toasted: the toast is gone by the time the
      // user has read a sentence, and this failure asks them to change something.
      setSubmitError(message);
      toast.error(t('cv.create.errors.failedTitle'), message);
    }
  }, [
    haptics,
    importEvidenceId,
    parsed,
    resolvedTitle,
    router,
    selectedSkill,
    skillFieldValues,
    skillInputFieldList,
    state,
    submit,
    t,
    toast,
  ]);

  const goNext = useCallback(() => {
    setSubmitError(null);
    if (step === 'source') {
      const action = resolveCreateCvSourceStepAction({
        uploadIncomplete,
        draftPending: resumeDraftPending,
        resumeBusy,
      });
      if (action === 'submit_resume' && composerSubmit.current) {
        // The resume is in the box but unread. Continue means "use this", so
        // read it here instead of pointing the user at a second button.
        haptics.trigger('light');
        composerSubmit.current();
        return;
      }
      if (action !== 'proceed') {
        toast.error(t('cv.create.errors.resumeRequired'));
        return;
      }
      if (!validation.canContinue) {
        toast.error(t(`cv.create.errors.${validation.errorKey ?? 'sourceRequired'}`));
        return;
      }
    }
    if (primary.intent === 'continue') {
      haptics.trigger('light');
      patch({ step: 'tailor' });
      return;
    }
    if (!validation.canSubmit) {
      toast.error(t(`cv.create.errors.${validation.errorKey ?? 'sourceRequired'}`));
      return;
    }
    void create();
  }, [
    create,
    haptics,
    patch,
    primary.intent,
    resumeBusy,
    resumeDraftPending,
    step,
    t,
    toast,
    uploadIncomplete,
    validation.canContinue,
    validation.canSubmit,
    validation.errorKey,
  ]);

  const goBack = useCallback(() => {
    haptics.trigger('light');
    if (step === 'tailor') {
      patch({ step: 'source' });
      return;
    }
    router.back();
  }, [haptics, patch, router, step]);

  return {
    // state
    state,
    step,
    stepIndex,
    totalSteps,
    hasTailorStep,
    selectedSkill,
    parsed,
    needsJobDescription,
    sourceSummary,
    submitError,

    // declared skill inputs (tabbio/v2 run-screen fields)
    skillInputFields: skillInputFieldList,
    skillFieldValues,
    setSkillFieldValue,
    skillDetailLoading,

    // data
    cvs: orderedCvs,
    cvsLoading: cvs.isLoading || autoApplyOverview.isLoading,
    hasCvs: orderedCvs.length > 0,

    // edits
    setSource,
    setSourceCv,
    setTitle,
    handleResumeParsed,
    handleResumeDraftChange,
    handleResumeBusyChange,
    composerSubmit,
    resumeBusy,
    resetResume,
    setTailorMode,
    setSkill,
    setJobDescription,

    // navigation
    goNext,
    goBack,
    canGoBack: step === 'tailor',

    // primary button
    primaryLabel: t(`cv.create.actions.${primary.labelKey}`),
    primaryDisabled: primary.disabled,
    isContinue: primary.intent === 'continue',
    isAiSubmit: primary.isAi,
    isSubmitting,
    isGenerating,
  };
}

export type CreateCvFlow = ReturnType<typeof useCreateCvFlow>;
